import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { BookOpen, Pencil, Download, Image as ImageIcon, ListChecks, EyeOff, Eye, Loader2, Printer } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { HUB_PROPERTIES, hubUrl } from '../pages/guest-hub/hubConfig';
import { QR_LOGO_DATA_URI } from '../pages/guest-hub/HubBrand';
import { downloadPNG } from '../pages/PropertyQRCodes';
import { SECTION_LABELS } from './guidebook/SectionOrderCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuidebookRow = ({ prop, stats }) => {
  const qrId = `gb-list-qr-${prop.slug}`;
  return (
    <Card data-testid={`admin-guidebook-row-${prop.slug}`}>
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-1.5 self-start">
          <QRCodeSVG id={qrId} value={hubUrl(prop.slug, 'guidebook')} size={72} level="H" marginSize={1} fgColor="#0f172a"
            imageSettings={{ src: QR_LOGO_DATA_URI, height: 18, width: 18, excavate: true }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><BookOpen className="h-4 w-4 text-amber-500" />{prop.name}</h3>
          <p className="text-xs text-slate-500 break-all">{hubUrl(prop.slug, 'guidebook')}</p>
          <div className="flex flex-wrap gap-3 text-xs text-slate-600 mt-2">
            <span className="inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" />{stats?.rules ?? 0} rules</span>
            <span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{stats?.photos ?? 0} photos/videos</span>
            <span>{stats?.custom_sections ?? 0} custom sections</span>
            {stats?.hidden > 0 && <span className="inline-flex items-center gap-1 text-slate-400"><EyeOff className="h-3.5 w-3.5" />{stats.hidden} hidden</span>}
            {stats?.updated_at && <span className="text-slate-400">Updated {new Date(stats.updated_at).toLocaleDateString()}</span>}
          </div>
          <div className="flex flex-wrap gap-3 text-xs mt-1.5" data-testid={`gb-row-stats-${prop.slug}`}>
            <span className="inline-flex items-center gap-1 text-slate-900 font-medium"><Eye className="h-3.5 w-3.5 text-amber-500" />{stats?.stats?.opens ?? 0} opens</span>
            <span className="text-slate-500">{stats?.stats?.opens_30d ?? 0} in last 30 days</span>
            <span className="text-slate-500">{stats?.stats?.unique_visitors ?? 0} guests</span>
            {stats?.stats?.sections?.[0] && <span className="text-slate-500">Top: {SECTION_LABELS[stats.stats.sections[0].section] || 'Custom section'} ({stats.stats.sections[0].count})</span>}
          </div>
        </div>
        <div className="flex gap-2 sm:flex-col">
          <Link to={`/admin/guidebooks/${prop.id}`} className="flex-1"><Button data-testid={`gb-edit-${prop.slug}`} size="sm" className="w-full bg-slate-900 hover:bg-slate-800"><Pencil className="h-4 w-4 mr-1" />Edit</Button></Link>
          <Button data-testid={`gb-list-qr-png-${prop.slug}`} size="sm" variant="outline" onClick={() => downloadPNG(qrId, `${prop.slug}-guidebook-qr`)}><Download className="h-4 w-4 mr-1" />QR</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminGuidebooks = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/guidebooks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setStats(Object.fromEntries(rows.map((r) => [r.property_id, r]))));
  }, [token]);

  if (!stats) return <Loader2 className="h-8 w-8 animate-spin text-slate-500" />;

  return (
    <div data-testid="admin-guidebooks-page" className="space-y-4">
      <p className="text-slate-600 text-sm">One guidebook per property. Edit rules, how-tos, photos and videos – guests see changes instantly after scanning the QR code.</p>
      <Link to="/qr-signs?mode=guidebook" target="_blank" data-testid="gb-all-signs-link" className="inline-flex items-center gap-1 text-sm text-amber-600 hover:underline"><Printer className="h-4 w-4" />Print "Scan for House Guidebook" signs for all properties</Link>
      {HUB_PROPERTIES.map((p) => <GuidebookRow key={p.id} prop={p} stats={stats[p.id]} />)}
    </div>
  );
};

export default AdminGuidebooks;
